import React from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  ArrowLeftRight,
  CheckCircle2,
  RefreshCcw,
  ShieldCheck,
  TrendingUp,
  Users,
  XCircle,
} from "lucide-react";
import { useAdminOverview } from "../../hooks/useAdmin";
import { AttentionPanel } from "./AttentionPanel";
import { usePermissions, PERMISSIONS } from "../../hooks/usePermissions";

export function AdminOverview() {
  const { data, isLoading, error, refetch, isFetching } = useAdminOverview();
  const metrics = data?.metrics;

  // A number on a dashboard that cannot be opened is trivia. Each card links
  // to the exact filtered list behind it - but only where the reader has
  // permission to open that list, so no card is a dead end.
  const { can } = usePermissions();
  const usersLink = can(PERMISSIONS.USERS_READ);
  const transfersLink = can(PERMISSIONS.TRANSFERS_READ);
  const complianceLink = can(PERMISSIONS.COMPLIANCE_READ);

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0A2540]/40 mb-1">
            Live system metrics
          </h2>
          <h1 className="text-2xl md:text-3xl text-[#0A4730] font-serif font-semibold">
            Overview Dashboard
          </h1>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-[#0A4730] disabled:opacity-50"
        >
          <RefreshCcw size={14} className={isFetching ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {error && <AdminError message="Could not load the admin overview." />}

      {/* Above the metrics on purpose: an operator opens this page to find out
          what needs doing, not to read totals. */}
      <AttentionPanel />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Active users"
          value={isLoading ? "—" : `${metrics?.active_users ?? 0} / ${metrics?.total_users ?? 0}`}
          caption="Active / total accounts"
          icon={Users}
          to={usersLink ? "/admin/users?account_status=active" : null}
        />
        <MetricCard
          title="Transfers (30d)"
          value={isLoading ? "—" : metrics?.transfers_last_30_days ?? 0}
          caption="Created in the last 30 days"
          icon={Activity}
          to={transfersLink ? "/admin/transfers" : null}
        />
        <MetricCard
          title="Pending transfers"
          value={isLoading ? "—" : metrics?.pending_transfers ?? 0}
          caption="Currently in a non-terminal state"
          icon={ArrowLeftRight}
          to={transfersLink ? "/admin/transfers?status=processing" : null}
        />
        <MetricCard
          title="Compliance reviews"
          value={isLoading ? "—" : metrics?.compliance_reviews ?? 0}
          caption="KYC or compliance attention needed"
          icon={ShieldCheck}
          to={complianceLink ? "/admin/compliance" : null}
        />
      </div>

      {/* Money view. Volume is reported per currency and never summed across
          them - there is no reporting FX layer, so a blended total would be
          arithmetically meaningless. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <MetricCard
          title="Completed (30d)"
          value={isLoading ? "—" : metrics?.completed_transfers_30_days ?? 0}
          caption="Successfully delivered transfers"
          icon={CheckCircle2}
        />
        <MetricCard
          title="Failed (30d)"
          value={isLoading ? "—" : metrics?.failed_transfers_30_days ?? 0}
          caption="Failed, cancelled, or refunded"
          icon={XCircle}
          to={transfersLink ? "/admin/transfers?status=unsuccessful" : null}
        />
        <MetricCard
          title="Success rate (30d)"
          value={
            isLoading
              ? "—"
              : metrics?.success_rate_30_days == null
              ? "No data"
              : `${(metrics.success_rate_30_days * 100).toFixed(1)}%`
          }
          caption="Completed vs. all terminal transfers"
          icon={TrendingUp}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CurrencyTable
          title="Completed volume by currency (30d)"
          rows={metrics?.volume_by_currency ?? []}
          isLoading={isLoading}
          emptyText="No completed transfers in the last 30 days."
        />
        <CurrencyTable
          title="Fee revenue by currency (30d)"
          rows={metrics?.fees_by_currency ?? []}
          isLoading={isLoading}
          emptyText="No fees recorded in the last 30 days."
        />
      </div>

      <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)]">
        <h3 className="text-sm font-semibold text-[#0A2540] mb-4">Recent System Activity</h3>
        {isLoading ? (
          <p className="text-sm text-slate-400 py-6">Loading activity…</p>
        ) : data?.recent_activity?.length ? (
          <div className="space-y-3">
            {data.recent_activity.map((item) => (
              <ActivityRow key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400 py-6">No activity has been recorded yet.</p>
        )}
      </div>
    </div>
  );
}

/**
 * A metric card, optionally a link to the list it summarises.
 *
 * `to` is omitted when the reader lacks permission for the destination, so a
 * finance seat sees the compliance count without being offered a click that
 * would only 403.
 */
function MetricCard({ title, value, caption, icon: Icon, to = null }) {
  const Wrapper = to ? Link : "div";
  const wrapperProps = to
    ? { to, className: "block group focus:outline-none focus:ring-4 focus:ring-[#0A4730]/10 rounded-3xl" }
    : {};

  return (
    <Wrapper {...wrapperProps}>
    <div
      className={`bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-4 h-full ${
        to ? "transition group-hover:border-[#0A4730]/30 group-hover:shadow-md" : ""
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0A2540]/40">
          {title}
        </span>
        <div className="w-9 h-9 rounded-xl bg-[#C0F6B7]/40 text-[#0A4730] flex items-center justify-center">
          <Icon size={18} />
        </div>
      </div>
      <h4 className="text-2xl font-serif font-semibold text-[#0A4730]">{value}</h4>
      <p className="text-xs text-slate-400">
        {caption}
        {to && (
          <span className="block mt-1 font-semibold text-[#0A4730]/0 group-hover:text-[#0A4730] transition">
            View list →
          </span>
        )}
      </p>
    </div>
    </Wrapper>
  );
}

function CurrencyTable({ title, rows, isLoading, emptyText }) {
  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)]">
      <h3 className="text-sm font-semibold text-[#0A2540] mb-4">{title}</h3>
      {isLoading ? (
        <p className="text-sm text-slate-400 py-4">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-400 py-4">{emptyText}</p>
      ) : (
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="pb-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0A2540]/40">
                Currency
              </th>
              <th className="pb-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0A2540]/40 text-right">
                Amount
              </th>
              <th className="pb-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0A2540]/40 text-right">
                Transfers
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => (
              <tr key={row.currency}>
                <td className="py-3 text-sm font-semibold text-[#0A4730]">
                  {row.currency}
                </td>
                <td className="py-3 text-sm text-[#0A2540] text-right tabular-nums">
                  {formatAmount(row.amount)}
                </td>
                <td className="py-3 text-sm text-slate-500 text-right tabular-nums">
                  {row.transfer_count}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function formatAmount(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function ActivityRow({ item }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-4 rounded-2xl bg-[#FDFDFD] border border-slate-100 text-left">
      <div>
        <p className="text-sm font-medium text-[#0A2540]">{item.title}</p>
        <span className="text-xs text-slate-400">{formatDate(item.occurred_at)}</span>
      </div>
      <span className="self-start sm:self-auto px-3 py-1 rounded-full text-xs font-semibold bg-[#C0F6B7]/30 text-[#0A4730] capitalize">
        {humanize(item.status)}
      </span>
    </div>
  );
}

function AdminError({ message }) {
  return <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-sm text-red-600">{message}</div>;
}

function humanize(value = "") {
  return value.replaceAll("_", " ");
}

function formatDate(value) {
  return value ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "—";
}
